/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { CaretRight } from '@carbon/react/icons';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import withActionHandler from '../utils/withActionHandler';
import { cleanUpKey, highlightCleaner } from '../utils/common';

function MenuDropdown({ displayName, selected, menuItems, onToggle = () => {}, to, nestedMenus = {}, isExpanded, highlightedQuery }) {
  const { t } = useTranslation();

  const [expanded, updateExpanded] = useState(false);

  // if isExpanded prop change, use that to drive state
  useEffect(() => {
    if (isExpanded !== null) {
      updateExpanded(isExpanded);
    }
  }, [isExpanded]);

  const toggleDisplay = withActionHandler(() => {
    updateExpanded(!expanded);
    onToggle(!expanded);
  });

  let highlight;
  if (highlightedQuery) {
    highlight = highlightCleaner(displayName, highlightedQuery);
  }

  const renderLink = (jsx) => {
    // only render react router link if clicking the dropdown takes the user somewhere
    if (to) {
      return (
        <Link
          className={classNames('menuDropdown tocHeader', { selected })}
          to={to}
          title={displayName}
          aria-label={displayName}
          data-testid="menuDropdown-navigate"
        >
          {jsx}
        </Link>
      );
    }
    return (
      <div className={classNames('menuDropdown tocHeader', { selected })} aria-label={displayName}>
        {jsx}
      </div>
    );
  };

  return (
    <div data-testid="menu-dropdown">
      <div
        className="clickable"
      >
        {renderLink(
          <>
            {/* eslint-disable-next-line react/button-has-type */}
            <button className={classNames('iconOnlyButton')} onClick={toggleDisplay} aria-label={t('menu_button')} data-testid="menuDropdown-button">
              <CaretRight size={32} className={classNames('expand-arrow', { rotate90: expanded })} />
            </button>
            <div className="menuDropdown__label truncate">
              <p className="truncate">{highlight ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlight) }} /> : displayName}</p>
            </div>
          </>
        )}
      </div>
      {expanded && (
        <ul>
          {nestedMenus &&
            Object.entries(nestedMenus).map(([key, value]) => (
              <li key={key} className="tocItem tocHeader" data-testid="grouped-menu-item">
                <MenuDropdown
                  className="nestedTag"
                  menuItems={value.menuItems}
                  displayName={key}
                  nestedMenus={value.nested}
                  onToggle={value.onToggle}
                  isExpanded={isExpanded}
                />
              </li>
            ))}
          {menuItems.map((menuItem) => {
            const itemKey = cleanUpKey(menuItem.displayName);

            return (
              <li className="tocItem" key={`${displayName}/${itemKey}`}>
                <Link
                  to={menuItem.route}
                  className={classNames('cds--side-nav__link', 'truncate toc-selectable menuDropdown__item', `toc-navigate-${cleanUpKey(itemKey)}`, {
                    selected: menuItem.selected
                  })}
                  title={menuItem.displayName}
                  data-testid="dropdown-item-list"
                >
                  <span className="cds--side-nav__link-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(menuItem.displayName) }} data-testid="grouped-menu-item" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

MenuDropdown.propTypes = {
  displayName: PropTypes.string, // display name of the dropdown
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      displayName: PropTypes.string, // page display name
      selected: PropTypes.bool, // if the item is in a selected state
      route: PropTypes.string // route to navigate to on click
    })
  ),
  nestedMenus: PropTypes.object, // keys are display names for nested dropdown, values are menuDropdown props
  className: PropTypes.string,
  onToggle: PropTypes.func, // function that triggers on dropdown toggle
  expanded: PropTypes.bool, // expanded prop used to drive external expand/collapse for functionality like expand all/collapse all.
  highlightedQuery: PropTypes.string // Optional HTML to show highlighted query
};

export default MenuDropdown;
